import { useState } from "react";
import type { FormEvent } from "react";
import { slug, stockLines } from "@lagerlog/core";
import { useCatalog, useLedger, useStore } from "../hooks.ts";
import { WORDS, MAIN_PROFILE, compareLagers } from "../brand.ts";

/**
 * Storages and their locations ("#/lager"): rename, add, take away what is empty, and choose
 * which storages this device holds. Works offline like every other change.
 */
export function Places() {
  const { store } = useStore();
  const catalog = useCatalog();
  const ledger = useLedger();
  const [newLager, setNewLager] = useState("");
  const [error, setError] = useState<string | null>(null);

  if (!catalog || !ledger) return null;
  const held = new Set(store.config?.lagers ?? []);
  // Locations of storages this device does not hold are known, their stock is not.
  const stock = new Map<string, number>();
  for (const l of stockLines(ledger)) stock.set(l.location, (stock.get(l.location) ?? 0) + Math.abs(l.qty));
  const byLager = compareLagers((id) => catalog.lagers.get(id)?.name ?? id);
  const lagers = [...catalog.lagers.values()].sort((a, b) => byLager(a.id, b.id));

  async function addLager(e: FormEvent) {
    e.preventDefault();
    const name = newLager.trim();
    if (!name) return;
    if (lagers.some((l) => l.name.toLowerCase() === name.toLowerCase())) return setError(`Ein Lager „${name}“ gibt es schon.`);
    // Ids over every storage of the household, also those of the other app (KellerLog or VorratsBuch), which this app does not show.
    const taken = new Set([...(store.replica?.entityView().values() ?? [])].filter((e) => e.type === "lager").map((e) => e.id));
    const id = slug(name, taken);
    await store.edit("lager", id, { name, profile: MAIN_PROFILE.id, archived: false });
    // With a first location, so it can be booked into at once (A2); renamed like any other.
    await store.edit("location", `${id}/${slug(WORDS.firstLocation, new Set())}`, { lager: id, name: WORDS.firstLocation, archived: false });
    await store.setLagers([...held, id]);
    setNewLager("");
    setError(null);
  }

  return (
    <div className="places">
      <a className="link" href="#/konto">
        ← Konto
      </a>
      <h2>Lager und Lagerorte</h2>
      <p className="muted">Ein Lager ist ein Raum wie {WORDS.lagerExample}, ein Lagerort ein Regal oder Fach darin. Änderungen gelten für alle Geräte, auch wenn sie ohne Netz gemacht werden.</p>
      {lagers.map((lager) => {
        const locations = [...catalog.locations.values()].filter((l) => l.lager === lager.id).sort((a, b) => a.name.localeCompare(b.name, "de", { numeric: true }));
        return (
          <section key={lager.id} className="card" data-testid={`lager-${lager.id}`}>
            <Rename value={lager.name} label="Lager" onSave={(name) => store.edit("lager", lager.id, { name })} testid={`lager-name-${lager.id}`} />
            <label className="check">
              <input
                type="checkbox"
                checked={held.has(lager.id)}
                onChange={(e) => store.setLagers(e.target.checked ? [...held, lager.id] : [...held].filter((id) => id !== lager.id))}
                disabled={held.has(lager.id) && held.size === 1}
              />
              auf diesem Gerät führen
            </label>
            {held.has(lager.id) ? (
              <ul className="list">
                {locations.map((loc) => {
                  const empty = !stock.get(loc.id);
                  return (
                    <li key={loc.id} className="entry">
                      <div className="entry-text">
                        <Rename value={loc.name} label="" onSave={(name) => store.edit("location", loc.id, { lager: lager.id, name })} testid={`location-name-${loc.id}`} />
                      </div>
                      <button
                        className="link danger entry-action"
                        disabled={!empty}
                        title={empty ? "" : "Hier liegt noch Bestand"}
                        onClick={() => {
                          if (confirm(`Lagerort „${loc.name}“ entfernen? Gebuchtes bleibt im Journal.`)) void store.edit("location", loc.id, { archived: true });
                        }}
                      >
                        Entfernen
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="muted">{locations.length} Lagerorte; Bestand und Änderungen nur auf Geräten, die dieses Lager führen.</p>
            )}
            {held.has(lager.id) && locations.length === 0 && (
              <p className="warn" data-testid={`no-location-${lager.id}`}>
                Noch kein Lagerort: Ohne einen lässt sich hier nichts einlagern. Leg unten einen an.
              </p>
            )}
            {held.has(lager.id) && <AddLocation lager={lager.id} taken={new Set([...catalog.locations.keys()].filter((id) => id.startsWith(`${lager.id}/`)).map((id) => id.slice(lager.id.length + 1)))} />}
            {locations.some((l) => stock.get(l.id)) && <p className="hint">Einen Lagerort mit Bestand erst leer räumen (umlagern), dann lässt er sich entfernen.</p>}
          </section>
        );
      })}
      <form className="card" noValidate onSubmit={addLager}>
        <label>
          Neues Lager
          <span className="row">
            <input value={newLager} onChange={(e) => setNewLager(e.target.value)} maxLength={40} placeholder="z. B. Klimaschrank" data-testid="new-lager" />
            <button className="primary" disabled={!newLager.trim()} data-testid="add-lager">
              Anlegen
            </button>
          </span>
        </label>
        {error && <p className="error-box">{error}</p>}
      </form>
    </div>
  );
}

function AddLocation({ lager, taken }: { lager: string; taken: Set<string> }) {
  const { store } = useStore();
  const [name, setName] = useState("");
  async function add(e: FormEvent) {
    e.preventDefault();
    const clean = name.trim();
    if (!clean) return;
    // A location removed earlier under the same id comes back.
    await store.edit("location", `${lager}/${slug(clean, taken)}`, { lager, name: clean, archived: false });
    setName("");
  }
  return (
    <form noValidate onSubmit={add} className="row">
      <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} placeholder="z. B. Regal D" data-testid={`new-location-${lager}`} />
      <button disabled={!name.trim()} data-testid={`add-location-${lager}`}>
        Hinzufügen
      </button>
    </form>
  );
}

/** A name that turns into a field on tap. */
function Rename({ value, label, onSave, testid }: { value: string; label: string; onSave: (name: string) => Promise<void>; testid: string }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(value);
  if (!editing) {
    return (
      <button className="rename" onClick={() => [setName(value), setEditing(true)]} data-testid={testid}>
        {label ? <strong>{value}</strong> : value} <span className="muted">✎</span>
      </button>
    );
  }
  return (
    <form
      className="row"
      noValidate
      onSubmit={async (e) => {
        e.preventDefault();
        if (name.trim() && name.trim() !== value) await onSave(name.trim());
        setEditing(false);
      }}
    >
      <input value={name} onChange={(e) => setName(e.target.value)} onFocus={(e) => e.target.select()} maxLength={40} autoFocus data-testid={`${testid}-input`} />
      <button className="primary">OK</button>
    </form>
  );
}
